import type { ReferencedEntry } from 'contentful';
import React, { forwardRef, Ref } from 'react';
import cx from 'classnames';
import styles from './Media.module.scss';

import {
  MediaSourceType,
  HasExternalSources,
  HasImage,
  HasMedia,
  HasMobileImage,
  HasVideo,
  HasTabletImage,
} from 'api/contentful/contentTypes.cloverdotcom';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { ContentfulImage } from '../Contentful/ContentfulImage';
import { LazyLoader } from '../LazyLoader/LazyLoader';
import { BasicMedia, BasicMediaProps } from './BasicMedia';
import { VideoPlayer } from '@clover-growth/video-player';
import { Video } from 'components/Contentful/Video';

type MediaProps = Pick<BasicMediaProps, Exclude<keyof BasicMediaProps, 'asset' | 'video'>> & {
  entry?: ReferencedEntry<
    HasVideo & HasImage & HasTabletImage & HasMobileImage & HasExternalSources & HasMedia
  >;
  canRenderExternal?: boolean;
  videoPlayerClassName?: string;
};

const Media = forwardRef<Video | ContentfulImage, MediaProps>(
  ({ entry, canRenderExternal, autoPlay, videoPlayerClassName, ...otherProps }, ref) => {
    const { browser } = useBreakpoints() || {};
    if (!entry || !entry.fields) return null;

    const { image: defaultImage, tabletImage, mobileImage, media, video, youtubeId } = entry.fields;
    const { sourceType = '' } = (media && media.fields) || {};
    const isVimeo = sourceType === MediaSourceType.VIMEO;
    const isGrowthVideoPlayer = Boolean(videoPlayerClassName);

    const image =
      (browser.is.mobile && mobileImage) ||
      (browser.is.tablet && (tabletImage || mobileImage)) ||
      defaultImage;

    if (canRenderExternal && youtubeId) {
      return (
        <VideoPlayer
          videoUrl={youtubeId}
          playerType="youtube"
          autoplay={autoPlay}
          className={cx(styles.videoPlayer, videoPlayerClassName)}
        />
      );
    }

    const vimeoId = media?.fields?.source;
    if (canRenderExternal && isVimeo && vimeoId) {
      return (
        <VideoPlayer
          playerType="vimeo"
          videoUrl={vimeoId}
          autoplay={autoPlay}
          className={cx(styles.videoPlayer, videoPlayerClassName)}
        />
      );
    }

    const videoUrl = video?.fields.file.url;
    if (videoUrl && isGrowthVideoPlayer) {
      const videoPosterUrl = image?.fields.file.url;
      return (
        <VideoPlayer
          playerType="native"
          theme="white"
          inlineControls="over-player"
          videoUrl={videoUrl}
          posterUrl={videoPosterUrl}
          autoplay={autoPlay}
          className={videoPlayerClassName}
        />
      );
    }

    return (
      <LazyLoader>
        <BasicMedia ref={ref as Ref<Video | ContentfulImage>} asset={image} video={video} {...otherProps} />
      </LazyLoader>
    );
  },
);

type MediaType = typeof Media;

export { MediaProps, Media, MediaType };
